import * as React from 'react';
import { Dropdown, Radio, SelectionStatus, TextField } from '@symphony-ui/uitoolkit-components';
import useOption from 'components/customHooks';
import PopupContext from 'contexts/PopupContext';
import PopupButtons from './PopupButtons';
import { floatRegex } from '../regexes';
const AmountPopup = ({ data, hasOverride, save }) => {
    const { setBlockClose } = React.useContext(PopupContext);
    const qualifierOptions = useOption('/syndicate/overview/size-qualifier/options', { sort: false });
    const [value, setValue] = React.useState(() => {
        if (data?.override?.nominal?.amount?.size !== undefined) {
            const override = data.override.nominal.amount.size.value;
            return override !== undefined ? (override / 1000000).toString() : '';
        }
        const val = data?.data?.nominal?.amount?.size?.value;
        return val !== undefined ? (val / 1000000).toString() : '';
    });
    const [qualifier, setQualifier] = React.useState(data?.override?.nominal?.amount?.size !== undefined ?
        data.override.nominal.amount.size.qualifier
        : data?.data?.nominal?.amount?.size?.qualifier);
    const [minValue, setMinValue] = React.useState(() => {
        const overrideMinimum = data?.override?.nominal?.amount?.size?.range?.minimum;
        if (overrideMinimum !== undefined)
            return (overrideMinimum / 1000000).toString();
        const minimum = data?.data?.nominal?.amount?.size?.range?.minimum;
        if (minimum !== undefined)
            return (minimum / 1000000).toString();
        return '';
    });
    const [maxValue, setMaxValue] = React.useState(() => {
        const overrideMaximum = data?.override?.nominal?.amount?.size?.range?.maximum;
        if (overrideMaximum !== undefined)
            return (overrideMaximum / 1000000).toString();
        const maximum = data?.data?.nominal?.amount?.size?.range?.maximum;
        if (maximum !== undefined)
            return (maximum / 1000000).toString();
        return '';
    });
    const [type, setType] = React.useState(() => {
        if (data?.override?.nominal?.amount?.size?.isBenchmark)
            return 'benchmark';
        if (data?.override?.nominal?.amount?.size?.isSubBenchmark)
            return 'sub';
        if (data?.override?.nominal?.amount?.size?.value !== undefined)
            return 'value';
        if (data?.override?.nominal?.amount?.size?.range !== undefined)
            return 'range';
        if (data?.data?.nominal?.amount?.size?.isBenchmark)
            return 'benchmark';
        if (data?.data?.nominal?.amount?.size?.isSubBenchmark)
            return 'sub';
        if (data?.data?.nominal?.amount?.size?.value !== undefined)
            return 'value';
        if (data?.data?.nominal?.amount?.size?.range !== undefined)
            return 'range';
        return 'value';
    });
    const onChangeMaximum = React.useCallback((event) => {
        const newValue = event.target.value;
        if (floatRegex.exec(newValue) !== null) {
            setMaxValue(newValue);
        }
    }, []);
    const onChangeMinimum = React.useCallback((event) => {
        const newValue = event.target.value;
        if (floatRegex.exec(newValue) !== null) {
            setMinValue(newValue);
        }
    }, []);
    const onChangeValue = React.useCallback((event) => {
        const newValue = event.target.value;
        if (floatRegex.exec(newValue) !== null) {
            setValue(newValue);
        }
    }, []);
    const minRef = React.useRef(null);
    const valueRef = React.useRef(null);
    React.useEffect(() => {
        if (valueRef.current !== null) {
            valueRef.current.focus();
        }
    }, []);
    React.useEffect(() => {
        if (minRef.current !== null) {
            minRef.current.focus();
        }
    }, []);
    const onChangeQualifier = React.useCallback((event) => {
        const newValue = event.target.value?.value;
        setQualifier(newValue);
    }, []);
    const size = React.useMemo(() => {
        const size = {};
        switch (type) {
            case 'benchmark':
                size.isBenchmark = true;
                break;
            case 'sub':
                size.isSubBenchmark = true;
                break;
            case 'range':
                size.range = {
                    minimum: Number.parseInt(minValue) * 1000000,
                    maximum: Number.parseInt(maxValue) * 1000000
                };
                break;
            case 'value':
                size.value = Number.parseInt(value) * 1000000;
                size.qualifier = qualifier;
                break;
        }
        return size;
    }, [maxValue, minValue, qualifier, type, value]);
    const onMenuOpen = React.useCallback(() => {
        setBlockClose(true);
    }, [setBlockClose]);
    const onMenuClose = React.useCallback(() => {
        setBlockClose(false);
    }, [setBlockClose]);
    const onchangeType = React.useCallback((event) => {
        const target = event.target;
        const newType = target.name;
        setType(newType);
        setValue('');
        setMinValue('');
        setMaxValue('');
    }, []);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { style: { display: 'flex', marginBottom: '0.5rem' } },
            type === 'range' ? React.createElement(React.Fragment, null,
                React.createElement("div", { style: { width: '6rem' } },
                    React.createElement(TextField, { ref: minRef, label: "Minimum", onChange: onChangeMinimum, value: minValue })),
                React.createElement("div", { style: { marginLeft: '1rem', width: '6rem' } },
                    React.createElement(TextField, { label: "Maximum", onChange: onChangeMaximum, value: maxValue }))) : undefined,
            type === 'value' ? React.createElement(React.Fragment, null,
                React.createElement("div", { style: { width: '6rem' } },
                    React.createElement(TextField, { ref: valueRef, label: "Size (in mio)", onChange: onChangeValue, value: value })),
                React.createElement("div", { style: { alignSelf: 'end', marginLeft: '1rem', width: '8rem' } },
                    React.createElement(Dropdown, { onChange: onChangeQualifier, onMenuClose: onMenuClose, onMenuOpen: onMenuOpen, isInputClearable: true, options: qualifierOptions, value: qualifierOptions.filter(o => 'value' in o).find(o => o.value === qualifier) }))) : undefined),
        React.createElement("div", { style: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: '0.5rem' } },
            React.createElement(Radio, { label: "Value", name: "value", onChange: onchangeType, status: type === 'value' ? SelectionStatus.CHECKED : SelectionStatus.UNCHECKED, value: type === 'value' ? 'true' : 'false' }),
            React.createElement(Radio, { label: "Benchmark", name: "benchmark", onChange: onchangeType, status: type === 'benchmark' ? SelectionStatus.CHECKED : SelectionStatus.UNCHECKED, value: type === 'benchmark' ? 'true' : 'false' }),
            React.createElement(Radio, { label: "Range", name: "range", onChange: onchangeType, status: type === 'range' ? SelectionStatus.CHECKED : SelectionStatus.UNCHECKED, value: type === 'range' ? 'true' : 'false' }),
            React.createElement(Radio, { label: "Sub Benchmark", name: "sub", onChange: onchangeType, status: type === 'sub' ? SelectionStatus.CHECKED : SelectionStatus.UNCHECKED, value: type === 'sub' ? 'true' : 'false' })),
        React.createElement("div", { style: { display: 'flex', justifyContent: 'right', marginTop: '0.5rem' } },
            React.createElement(PopupButtons, { isDisabled: false, value: size, hasOverride: hasOverride, save: save }))));
};
export default AmountPopup;
